/* eslint-disable react/no-children-prop */
/* eslint-disable react/jsx-curly-brace-presence */
import { useLazyQuery } from "@apollo/client";
import { Badge, Image, message, Tag, Tooltip } from "antd";
import dayjs from "dayjs";
import { isEmpty, isUndefined } from "lodash";
import { LuCar, LuMail, LuPhone, LuUsers } from "react-icons/lu";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { BASE_URL_FOR_STATIC_ICONS_AND_IMAGES, forum } from "../../../config";
import { GET_MORE_APPOINTMENT_DETAILS_ACTIVITY_CENTER } from "../../../graphql/query";
import useCrmIntegration from "../../../hooks/useCrmIntegration";
import useLang from "../../../hooks/useLang";
import { DealInfoText } from "../../../library/activityComponents";
import { convertUtcToLocal, firstUpperCaseWordParser } from "../../../library/utils";
import CustomLabel from "../../commonComponents/label";
import { getLeadOpportunitySource } from "../leadSourceHelper";
import { getDefaultOpportunity } from "../../../helpers/opportunityHelper";
import {
  getMostRecentPermittedRootOppTypeId,
  getMostRecentPermittedRootOppTypeIdInFilter,
  oppMatchesTab,
} from "../../../library/oppTypeScoping";
import { displayContactInfo, getLeadStatus } from "./engagementHelpers";
import ConsentPlayLogo from "../../../icons/svg/play-consent.svg";
import ConsentPauseLogo from "../../../icons/svg/pause-consent.svg";
import ConsentTimerLogo from "../../../icons/svg/timer-consent.svg";
import { CONSENT_DESCRIPTION, CONSENT_STATUS } from "../../../constant/LeadConstant";

const utc = require("dayjs/plugin/utc");
const timezone = require("dayjs/plugin/timezone"); // dependent on utc plugin

dayjs.extend(utc);
dayjs.extend(timezone);

// Shared tint for the lead card's meta-row icons (contact / owners / vehicle).
const CARD_ICON_COLOR = "#334155";

/**
 * Every meta-row icon renders inside the same fixed 16px box with a single
 * size + stroke weight. Rendering the lucide glyphs bare made them look
 * mismatched: each has its own glyph density inside the 24px viewBox, and the
 * rows they sit in aligned differently (baseline vs center), so the phone, user
 * and car read as three different sizes.
 */
const CardMetaIcon = ({ icon: Icon }) => (
  <span className="flex h-4 w-4 shrink-0 items-center justify-center">
    <Icon color={CARD_ICON_COLOR} strokeWidth={1.75} />
  </span>
);

const LeadList = props => {
  const {
    messageObject,
    user,
    item,
    handleAppointmentModalClose,
    spinner,
    selectedLeadId,
    selectedLead,
    activeOpp,
    selectedOppTypeId,
    // Root opp-type ids the parent getLeads query is currently
    // filtered to (after walking leaf → root). When non-empty, the
    // lead row's "most recent opp" preview is scoped to that filter
    // so a multi-tab lead doesn't display its Sales opp data when
    // the user filtered the list to Service.
    listFilterRootOppTypeIds = [],
  } = props;
  const crmIntegration = useCrmIntegration();
  const [lang] = useLang();
  const [vehicle, setvehicle] = useState("");

  const renderContactInfo = () => {
    const contact = displayContactInfo(item, crmIntegration);
    return (
      <div className="flex items-center gap-2">
        <CardMetaIcon icon={contact.type === "email" ? LuMail : LuPhone} />
        <CustomLabel label={contact.value} labelClass="text-sm font-sans text-grey-700" />
      </div>
    );
  };

  // Each lead card normally shows its OWN most-recent permitted root
  // opp's data — derived from ``item.leadOpportunities`` already in
  // the getLeads payload. The global ``selectedOppTypeId`` doesn't
  // leak into other rows, so clicking a lead doesn't cause unrelated
  // cards to flicker.
  //
  // Exception: when THIS lead is the currently-selected one (open in
  // the detail panel) AND the user has explicitly switched tabs there,
  // the card honors that tab so the selected row stays in sync with
  // what's visible on the right.
  const isThisLeadSelected = +item?.id === +selectedLead?.id;
  const effectiveOppTypeId = useMemo(() => {
    if (isThisLeadSelected && selectedOppTypeId) return selectedOppTypeId;
    // Scope the row's preview to the list's active opp-type filter
    // when one is applied — otherwise a multi-tab lead's row would
    // show its absolute most-recent opp (which can be in a different
    // root than the user filtered for) and look mis-tabbed.
    return getMostRecentPermittedRootOppTypeIdInFilter(item, user, listFilterRootOppTypeIds);
  }, [isThisLeadSelected, selectedOppTypeId, item, user, listFilterRootOppTypeIds]);

  const scopedLeadOpps = useMemo(() => {
    if (!effectiveOppTypeId) return item?.leadOpportunities || [];
    return (item?.leadOpportunities || []).filter(opp => oppMatchesTab(opp, effectiveOppTypeId));
  }, [item?.leadOpportunities, effectiveOppTypeId]);

  const scopedSelectedLeadOpps = useMemo(() => {
    if (!selectedOppTypeId) return selectedLead?.leadOpportunities || [];
    return (selectedLead?.leadOpportunities || []).filter(opp =>
      oppMatchesTab(opp, selectedOppTypeId)
    );
  }, [selectedLead?.leadOpportunities, selectedOppTypeId]);

  const findOpp = () => {
    return scopedSelectedLeadOpps.find(item => +item.id === +activeOpp);
  };
  const opportunityObj = useMemo(findOpp, [activeOpp, scopedSelectedLeadOpps]);
  const defaultOpportunity = useMemo(() => getDefaultOpportunity(scopedLeadOpps), [scopedLeadOpps]);

  // For the row that is currently open in the detail panel, read the owners
  // from the redux `selectedLead` instead of the list payload: that copy is
  // refreshed on every opportunity edit, so the open card reflects owner
  // changes immediately rather than waiting for the next getLeads.
  const selectedLeadDefaultOpportunity = useMemo(
    () => getDefaultOpportunity(scopedSelectedLeadOpps),
    [scopedSelectedLeadOpps]
  );

  // Owners of the opp this card is previewing — same `defaultOpportunity` the
  // vehicle line uses, so the row stays internally consistent when the user
  // switches opp-type tabs. Both payloads now expand `userType` and `isActive`
  // on `leadOpportunityUsers`, so everything is read straight off the row.
  // Entries without a resolvable name are dropped (a userId with no expanded
  // `user` would otherwise render a blank chip).
  const opportunityUsers = useMemo(() => {
    const sourceOpportunity =
      isThisLeadSelected && selectedLeadDefaultOpportunity
        ? selectedLeadDefaultOpportunity
        : defaultOpportunity;

    return (sourceOpportunity?.leadOpportunityUsers || [])
      .filter(oppUser => !!oppUser?.isActive)
      .map(oppUser => ({
        key: oppUser?.id,
        name: oppUser?.user?.fullName,
        role: oppUser?.user?.companyRole?.name,
        userType: oppUser?.userType?.name,
      }))
      .filter(oppUser => !isEmpty(oppUser.name));
  }, [isThisLeadSelected, selectedLeadDefaultOpportunity, defaultOpportunity]);

  // "Name (Role - User type)", degrading gracefully when either part is absent.
  const formatOpportunityUser = owner => {
    const detail = [owner.role, owner.userType].filter(Boolean).join(" - ");
    return detail ? `${owner.name} (${detail})` : owner.name;
  };

  const parseVehicles = () => {
    const latestOpportunity = defaultOpportunity;
    const vehicles = latestOpportunity?.vehiclesOfInterest;
    const voi = vehicles && vehicles.length > 0 && (vehicles.find(v => v.isPrimary) || vehicles[0]);
    if (voi) {
      let name = "";
      if (voi?.year && voi.year !== 0 && voi.year !== "" && voi.year !== "0")
        name = `${name} ${voi.year}`;
      if (voi?.make && voi.make !== 0 && voi.make !== "" && voi.make !== "0")
        name = `${name} ${voi.make}`;
      if (voi?.model && voi.model !== 0 && voi.model !== "" && voi.model !== "0")
        name = `${name} ${voi.model}`;
      return isEmpty(name) ? setvehicle("No vehicle") : setvehicle(name);
    }
    return setvehicle("No vehicle");
  };

  useEffect(() => {
    parseVehicles();
    // ``parseVehicles`` reads ``defaultOpportunity`` which is scoped by
    // ``effectiveOppTypeId`` (selectedOppTypeId or the BE-supplied
    // mostRecentPermittedRootOppTypeId fallback). Include it so the
    // displayed vehicle updates both on tab switch AND on initial
    // render when the BE fallback resolves a different root.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item?.leadVehicleInventory, activeOpp, effectiveOppTypeId]);

  // ``crmLeadObj.activeLeadOpportunity`` reflects whatever the CRM says
  // is active, ignoring our tab scoping. When a tab is selected and the
  // CRM-active opp doesn't match the tab's root, fall through to the
  // scoped defaultOpportunity so the card stays consistent with the
  // tab.
  const rawCrmActiveOppr = !isEmpty(item?.crmLeadObj?.activeLeadOpportunity)
    ? item.crmLeadObj.activeLeadOpportunity
    : null;
  const crmActiveOppr =
    effectiveOppTypeId && rawCrmActiveOppr && !oppMatchesTab(rawCrmActiveOppr, effectiveOppTypeId)
      ? null
      : rawCrmActiveOppr;
  const latestOpp =
    crmActiveOppr ||
    (opportunityObj && item.id === selectedLead?.id ? opportunityObj : defaultOpportunity);

  const leadSource = {
    name: latestOpp?.opportunitySource?.name || "N/A",
    tooltipSourceMsg: getLeadOpportunitySource(latestOpp?.opportunitySource || {}),
  };

  // Returns clean text content from a message, stripping HTML tags only for emails
  const getMessagePreviewText = (content, isEmail) => {
    if (!content) return "";
    if (!isEmail) return content;
    return content
      .replace(/<[^>]*>/g, "")
      .replace(/&nbsp;/gi, " ")
      .replace(/\s+/g, " ")
      .trim();
  };

  // Scope ``activeAppointment`` to the tab.
  //
  // Preferred source: ``item.activeAppointmentsByRootOppType`` — a BE
  // dataloader returns the most-recent appointment PER root opp_type
  // for the lead, so we can pick the right one for whichever tab is
  // active. Falls back to ``item.activeAppointment`` (legacy) when the
  // tab-scoped list is unavailable, in which case we still drop it if
  // the legacy single appointment doesn't belong to the active tab.
  const scopedActiveAppointment = useMemo(() => {
    if (!effectiveOppTypeId) return item?.activeAppointment || null;
    const perTab = item?.activeAppointmentsByRootOppType;
    if (Array.isArray(perTab) && perTab.length > 0) {
      const hit = perTab.find(e => +e?.rootOppTypeId === +effectiveOppTypeId);
      if (hit?.appointment) return hit.appointment;
      return null;
    }
    const apt = item?.activeAppointment;
    if (!apt) return null;
    const scopedOppIds = new Set(scopedLeadOpps.map(o => +o.id));
    return scopedOppIds.has(+apt.leadOpportunityId) ? apt : null;
  }, [
    item?.activeAppointment,
    item?.activeAppointmentsByRootOppType,
    effectiveOppTypeId,
    scopedLeadOpps,
  ]);

  const appointmentStatus =
    scopedActiveAppointment?.appointmentStatus === "CANCELLED"
      ? "error"
      : scopedActiveAppointment?.appointmentStatus === "SHOWED"
      ? "show"
      : scopedActiveAppointment?.appointmentStatus === "NO_SHOWED"
      ? "noshow"
      : scopedActiveAppointment?.isConfirmed
      ? "success"
      : dayjs(scopedActiveAppointment?.startDatetime).format("MM/DD/YYYYTHH:mm") <
        dayjs().utc().format("MM/DD/YYYYTHH:mm")
      ? "default"
      : "processing";

  const appointmentTooltip = useMemo(() => {
    if (!scopedActiveAppointment?.startDatetime) return "";
    return (
      <>
        <p>
          Appointment:{" "}
          {!isUndefined(scopedActiveAppointment?.startDatetime)
            ? convertUtcToLocal(
                scopedActiveAppointment?.startDatetime,
                scopedActiveAppointment?.extraData?.is_tentative_appointment_time
                  ? "ddd, MMMM Do YYYY"
                  : "ddd, MMMM Do YYYY, h:mm A",
                scopedActiveAppointment?.timezone
              )
            : "NA"}
        </p>
        <p>
          {lang.status}:{" "}
          {firstUpperCaseWordParser(scopedActiveAppointment?.appointmentStatus) || "NA"}
        </p>
      </>
    );
  }, [scopedActiveAppointment, lang.status]);

  const setLeadStatus = leadItem => {
    const status = { leadStatus: "", toolTipLeadStatus: "" };
    const latestOpportunity =
      crmActiveOppr ||
      (opportunityObj && item.id === selectedLead?.id ? opportunityObj : defaultOpportunity);

    status.toolTipLeadStatus = latestOpportunity?.opportunityStatus?.parentStatus
      ? `${latestOpportunity?.opportunityStatus?.parentStatus?.name} / ${
          latestOpportunity?.opportunityStatus?.name || ""
        }`.replace(/_/g, " ")
      : latestOpportunity?.opportunityStatus?.name || "";

    status.leadStatus = latestOpportunity?.opportunityStatus?.name
      ? getLeadStatus(
          user,
          latestOpportunity?.opportunityStatus?.name,
          status.toolTipLeadStatus,
          +selectedLeadId === +leadItem?.id ? spinner : false
        )
      : leadItem?.leadStatusType?.type
      ? getLeadStatus(
          user,
          leadItem?.leadStatusType?.type,
          status.toolTipLeadStatus,
          +selectedLeadId === +leadItem?.id ? spinner : false
        )
      : "";
    return status;
  };

  const appointmentClass = status => {
    switch (status) {
      case "error":
        return "bg-red-50 border-error-500";
      case "default":
        return "bg-brand-50 border-brand-500";
      case "success":
        return "bg-green-50 border-green-500";
      case "noshow":
        return "bg-grey-50 border-grey-500";
      case "show":
        return "bg-brand-50 border-brand-500";
      default:
        return "bg-brand-50 border-brand-500";
    }
  };
  const [getAppointmntDetails, { loading: appointmentLoader }] = useLazyQuery(
    GET_MORE_APPOINTMENT_DETAILS_ACTIVITY_CENTER
  );
  const messageRef = useRef(null);

  useEffect(() => {
    if (appointmentLoader) messageRef.current = message.loading("Fetching appointment details", 0);
    else {
      messageRef.current && messageRef.current();
    }
  }, [appointmentLoader]);

  return (
    <div className="flex flex-col justify-center">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <Tooltip
            placement="topLeft"
            title={leadSource?.tooltipSourceMsg || item?.otherSource || "N/A"}
          >
            <DealInfoText
              lineHight={0}
              // `w-fit` keeps the hover target on the logo + source name only.
              // As a block-level flex row it spanned the full card width, so the
              // tooltip fired anywhere along the empty space beside the source.
              className="flex flex-row items-center gap-2 py-1 text-sm text-[#333] w-fit"
            >
              {
                <img
                  width={
                    leadSource && leadSource?.name?.toLowerCase() === "cars.com" ? "38px" : "18px"
                  }
                  src={`/images/sources/${(leadSource?.name || "marketplace")
                    .toLowerCase()
                    .replace(".", "-")}.png`}
                  onError={e => {
                    e.target.src = BASE_URL_FOR_STATIC_ICONS_AND_IMAGES + forum;
                  }}
                  className="align-middle rounded h-[18px]"
                  alt={leadSource?.name}
                />
              }

              <CustomLabel
                label={
                  (leadSource?.name || "").length <= 25
                    ? leadSource?.name || "Direct"
                    : `${leadSource?.name.substring(0, 22)}...`
                }
                labelClass="text-sm font-sans font-[400] text-grey-700"
              />
            </DealInfoText>
          </Tooltip>
          {renderContactInfo()}

          {opportunityUsers.length > 0 && (
            <div className="flex items-center mt-1 gap-2">
              <CardMetaIcon icon={LuUsers} />
              {/* Tooltip sits on the name only, per spec — the full roster with
                  each user's company role is revealed on hover. */}
              <Tooltip
                placement="topLeft"
                // Wider than antd's default and `whitespace-nowrap` per row so a
                // "Name (Role - User type)" line isn't wrapped mid-phrase.
                overlayStyle={{ maxWidth: 420 }}
                overlayInnerStyle={{ maxWidth: 420 }}
                title={
                  <div className="flex flex-col gap-0.5">
                    {opportunityUsers.map(owner => (
                      <span key={owner.key} className="whitespace-nowrap">
                        {formatOpportunityUser(owner)}
                      </span>
                    ))}
                  </div>
                }
              >
                <span className="text-sm font-sans text-grey-700 cursor-default">
                  {opportunityUsers[0].name}
                  {opportunityUsers.length > 1 && (
                    <Tag className="ml-1 mr-0 px-1 leading-4 text-[11px]">
                      +{opportunityUsers.length - 1}
                    </Tag>
                  )}
                </span>
              </Tooltip>
            </div>
          )}

          {vehicle && !vehicle.includes("undefined") && (
            <div className="flex items-center mt-1 gap-2">
              <CardMetaIcon icon={LuCar} />
              <CustomLabel label={vehicle} labelClass="text-sm font-sans text-grey-700" />
            </div>
          )}
        </div>
        <div>
          <div className="flex flex-col items-end gap-1">
            <Badge count={item?.unreadCount || 0} />
            {
              // (item?.crmLeadObj?.crmStatus === "Open" ||
              //   (item?.leadStatusType?.type || "").includes("ACTIVE")) &&
              appointmentTooltip && (
                <Tooltip placement="topLeft" title={appointmentTooltip}>
                  <div
                    className={`flex-col-centered w-10 items-center cursor-pointer justify-center gap-0.5 px-2 py-1 rounded-[4px] border-[1px] border-solid ${appointmentClass(
                      appointmentStatus
                    )}`}
                    onClick={async e => {
                      const res = await getAppointmntDetails({
                        variables: {
                          appointmentId: scopedActiveAppointment.id,
                        },
                        fetchPolicy: "network-only",
                      });
                      const leadDetails = {
                        ...item,
                        activeAppointment: {
                          ...scopedActiveAppointment,
                          leadOpportunity: res?.data?.getAppointmentDetails?.leadOpportunity,
                          discussedVoi: res?.data?.getAppointmentDetails?.discussedVoi,
                          discussedVoiId: res?.data?.getAppointmentDetails?.discussedVoiId,
                          dateCreated: res?.data?.getAppointmentDetails?.dateCreated,
                          dateUpdated: res?.data?.getAppointmentDetails?.dateUpdated,
                          lead: {
                            leadVehicleInventory:
                              res?.data?.getAppointmentDetails?.lead?.leadVehicleInventory,
                            vehicleOfInterest:
                              res?.data?.getAppointmentDetails?.lead?.vehicleOfInterest,
                            crmLeadObj: res?.data?.getAppointmentDetails?.lead?.crmLeadObj,
                            fullName: res?.data?.getAppointmentDetails?.lead?.fullName,
                          },
                          extraData: res?.data?.getAppointmentDetails?.extraData,
                        },
                      };
                      handleAppointmentModalClose(e, leadDetails, true);
                    }}
                  >
                    <span className="text-grey-700 text-sm font-[600]">
                      {scopedActiveAppointment
                        ? convertUtcToLocal(
                            scopedActiveAppointment?.startDatetime,
                            "DD",
                            scopedActiveAppointment?.timezone
                          )
                        : "N/A"}
                    </span>
                    <span className="text-grey-700 text-sm font-[400]">
                      {scopedActiveAppointment
                        ? convertUtcToLocal(
                            scopedActiveAppointment?.startDatetime,
                            "MMM",
                            scopedActiveAppointment?.timezone
                          )
                        : ""}
                    </span>
                  </div>
                </Tooltip>
              )
            }
          </div>
        </div>
      </div>

      <div className="flex items-center mt-2 gap-1">
        {!isEmpty(messageObject) ? (
          <div className="flex-1 min-w-0 flex items-center">
            <Tooltip
              title={
                messageObject?.messageType !== "EMAIL" ? (
                  <span
                    className="[&_a]:!text-twLinkColor"
                    dangerouslySetInnerHTML={{
                      __html: getMessagePreviewText(
                        messageObject?.translatedContent || messageObject?.content,
                        false
                      ),
                    }}
                  />
                ) : (
                  <span>
                    {(() => {
                      const text = getMessagePreviewText(messageObject?.content, true);
                      return text.length > 300 ? `${text.slice(0, 300)}...` : text;
                    })()}
                  </span>
                )
              }
            >
              <p
                className={`${
                  selectedLead && +selectedLead?.id === +item?.id ? "bg-white" : "bg-[#F5F5F5]"
                } p-1 text-grey-700 text-[12px] font-[400] rounded-[3px] font-sans pl-2 flex items-center line-clamp-1 w-full`}
              >
                <span className="flex items-center gap-1">
                  <span className="flex-initial">
                    {messageObject?.direction === "TO_LEAD"
                      ? `${
                          messageObject?.user?.fullName ||
                          `${messageObject?.user?.firstName || ""} ${
                            messageObject?.user?.lastName || ""
                          }` ||
                          ""
                        }`
                      : messageObject?.direction === "FROM_OTTO"
                      ? "Otto bot"
                      : item?.fullName}{" "}
                  </span>
                  {(messageObject?.user?.fullName ||
                    messageObject?.user?.firstName ||
                    messageObject?.user?.lastName) &&
                    ":"}
                  <span
                    dangerouslySetInnerHTML={{
                      __html: getMessagePreviewText(
                        messageObject?.translatedContent || messageObject?.content,
                        messageObject?.messageType === "EMAIL"
                      ),
                    }}
                    className="line-clamp-1 flex-1 text-ellipsis overflow-hidden message-content-inline"
                  />
                </span>
              </p>
            </Tooltip>
          </div>
        ) : (
          <div className="flex-1 min-w-0">
            <p className="bg-[#F5F5F5] p-1 text-grey-700 text-[12px] font-[400] rounded-[3px] font-sans pl-2 line-clamp-1 w-full m-0">
              No conversation yet
            </p>
          </div>
        )}
        <div className="flex items-center ml-auto flex-shrink-0">
          <Tooltip
            title={
              CONSENT_DESCRIPTION[item?.campaignAutoResponse] || CONSENT_DESCRIPTION.DISENROLLED
            }
          >
            <Image
              src={
                CONSENT_STATUS.ENROLLED === item?.campaignAutoResponse
                  ? ConsentPlayLogo
                  : CONSENT_STATUS.PAUSED === item?.campaignAutoResponse
                  ? ConsentTimerLogo
                  : ConsentPauseLogo
              }
              alt="Consent Active"
              preview={false}
            />
          </Tooltip>
        </div>
      </div>

      <style jsx>{`
        .message-content-inline * {
          display: inline !important;
          margin: 0 !important;
          padding: 0 !important;
          border: none !important;
          background: none !important;
        }

        .message-content-inline br {
          display: none !important;
        }

        .message-content-inline div,
        .message-content-inline blockquote {
          display: inline !important;
        }
      `}</style>
    </div>
  );
};

export default LeadList;
